/**
 * The section registry for Layout U (the Section Lab).
 *
 * One entry per distinct block we've built across R, S and T. `from` records which letter it
 * came from, so while rearranging you can see whether an order is really "R with two bits of
 * S" or something new.
 *
 * Adding a section: add an id here and its content in SectionLabPage's CONTENT map. A saved
 * order that predates it still works — unknown ids are dropped and new ones are appended.
 */

export type LabSectionId =
  | "hero"
  | "heroTexts"
  | "heroA"
  | "bridgeB"
  | "etEmbedB"
  | "whatItDoes"
  | "liveEt"
  | "video"
  | "etControls"
  | "choose"
  | "premiere"
  | "timeline"
  | "formats"
  | "tiktok"
  | "upload"
  | "pricing";

export interface LabSection {
  id: LabSectionId;
  /** Shown in the reorder panel. */
  label: string;
  /** Which layout this block came from. */
  from: string;
}

export const LAB_SECTIONS: LabSection[] = [
  { id: "hero", label: "Hero + rotating headline", from: "R" },
  { id: "heroTexts", label: "★ All hero texts (pick one)", from: "lab" },
  { id: "heroA", label: "Hero — bold two-liner", from: "A" },
  { id: "whatItDoes", label: "What CherryClipz does", from: "new" },
  { id: "bridgeB", label: "CherryPick or Chat bridge", from: "B" },
  { id: "etEmbedB", label: "Inline editor embed", from: "B" },
  { id: "video", label: "Demo video", from: "T" },
  { id: "liveEt", label: "Live editor", from: "R/S" },
  { id: "etControls", label: "Editor + controls", from: "B (new)" },
  { id: "choose", label: "Choose: pick or ask AI", from: "S" },
  { id: "premiere", label: "Phone → Premiere", from: "S" },
  { id: "timeline", label: "Sheet → timeline", from: "R" },
  { id: "formats", label: "Every screen", from: "S" },
  { id: "tiktok", label: "Vertical / TikTok", from: "R" },
  { id: "upload", label: "Upload your own", from: "R" },
  { id: "pricing", label: "Pricing", from: "R/S" },
];

/** Font stacks to audition a headline in. Nothing here needs loading — Outfit is already in
 *  the app, the rest are system faces, so switching is instant and can't break the build. */
export const LAB_FONTS: { id: string; label: string; stack: string }[] = [
  { id: "outfit", label: "Outfit", stack: '"Outfit", system-ui, sans-serif' },
  { id: "system", label: "System", stack: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
  { id: "grotesk", label: "Grotesk", stack: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
  { id: "serif", label: "Serif", stack: 'Georgia, "Times New Roman", serif' },
  { id: "condensed", label: "Condensed", stack: '"Avenir Next Condensed", "HelveticaNeue-CondensedBold", Impact, sans-serif' },
  { id: "mono", label: "Mono", stack: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' },
  { id: "rounded", label: "Rounded", stack: '"SF Pro Rounded", "Varela Round", ui-rounded, system-ui, sans-serif' },
];

export const LAB_ORDER_KEY = "cc_lab_order";
export const LAB_FONT_KEY = "cc_lab_hero_font";
export const LAB_CUSTOM_KEY = "cc_lab_hero_custom";
export const LAB_HIDDEN_KEY = "cc_lab_hidden";
